import type { ClientPortalTask } from '@/lib/api/client-portal.api'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import { createSelectors } from './factories/createSelectors'

export type ClientPortalProjectTab = 'overview' | 'tasks' | 'docs' | 'updates'

export type ClientPortalTaskViewMode = 'table' | 'board' | 'timeline'

export type ClientPortalResourceList = 'notes' | 'links' | 'updates'

interface ClientPortalListFilters {
  search: string
  projectFilter: string
  typeFilter: string
}

interface ClientPortalState {
  mobileSidebarOpen: boolean
  /** Desktop sidebar mode: collapsed = icon rail with hover flyouts. Persisted per device, like the main app. */
  desktopSidebarCollapsed: boolean
  listFiltersByEntityId: Record<
    string,
    Partial<Record<ClientPortalResourceList, ClientPortalListFilters>>
  >
  activeProjectTabs: Record<string, ClientPortalProjectTab>
  taskViewModes: Record<string, ClientPortalTaskViewMode>
  selectedProjectTasks: Record<string, ClientPortalTask | null>
  projectInfoOpen: Record<string, boolean>
  threadSearchByEntityId: Record<string, string>
  threadDetailMobileOpenByEntityId: Record<string, boolean>

  setMobileSidebarOpen: (open: boolean) => void
  setDesktopSidebarCollapsed: (collapsed: boolean) => void
  setListFilters: (
    entityId: string,
    list: ClientPortalResourceList,
    filters: Partial<ClientPortalListFilters>,
  ) => void
  clearListFilters: (entityId: string, list: ClientPortalResourceList) => void
  setActiveProjectTab: (projectId: string, tab: ClientPortalProjectTab) => void
  setTaskViewMode: (projectId: string, mode: ClientPortalTaskViewMode) => void
  setSelectedProjectTask: (
    projectId: string,
    task: ClientPortalTask | null,
  ) => void
  updateSelectedProjectTask: (
    projectId: string,
    taskId: string,
    updates: Partial<ClientPortalTask>,
  ) => void
  setProjectInfoOpen: (projectId: string, open: boolean) => void
  setThreadSearch: (entityId: string, search: string) => void
  setThreadDetailMobileOpen: (entityId: string, open: boolean) => void
  resetPortalUi: (entityId?: string) => void
}

const DEFAULT_LIST_FILTERS: ClientPortalListFilters = {
  search: '',
  projectFilter: 'all',
  typeFilter: 'all',
}

function getScopedListFilters(
  state: ClientPortalState,
  entityId: string,
  list: ClientPortalResourceList,
): ClientPortalListFilters {
  return {
    ...DEFAULT_LIST_FILTERS,
    ...state.listFiltersByEntityId[entityId]?.[list],
  }
}

const store = create<ClientPortalState>()(
  persist(
    (set, get) => ({
      mobileSidebarOpen: false,
      desktopSidebarCollapsed: false,
      listFiltersByEntityId: {},
      activeProjectTabs: {},
      taskViewModes: {},
      selectedProjectTasks: {},
      projectInfoOpen: {},
      threadSearchByEntityId: {},
      threadDetailMobileOpenByEntityId: {},

      setMobileSidebarOpen: open => set({ mobileSidebarOpen: open }),

      setDesktopSidebarCollapsed: collapsed =>
        set({ desktopSidebarCollapsed: collapsed }),

      setListFilters: (entityId, list, filters) =>
        set(state => {
          const current = getScopedListFilters(state, entityId, list)
          return {
            listFiltersByEntityId: {
              ...state.listFiltersByEntityId,
              [entityId]: {
                ...state.listFiltersByEntityId[entityId],
                [list]: {
                  ...current,
                  ...filters,
                },
              },
            },
          }
        }),

      clearListFilters: (entityId, list) =>
        set(state => ({
          listFiltersByEntityId: {
            ...state.listFiltersByEntityId,
            [entityId]: {
              ...state.listFiltersByEntityId[entityId],
              [list]: DEFAULT_LIST_FILTERS,
            },
          },
        })),

      setActiveProjectTab: (projectId, tab) =>
        set(state => ({
          activeProjectTabs: {
            ...state.activeProjectTabs,
            [projectId]: tab,
          },
        })),

      setTaskViewMode: (projectId, mode) =>
        set(state => ({
          taskViewModes: {
            ...state.taskViewModes,
            [projectId]: mode,
          },
        })),

      setSelectedProjectTask: (projectId, task) =>
        set(state => ({
          selectedProjectTasks: {
            ...state.selectedProjectTasks,
            [projectId]: task,
          },
        })),

      updateSelectedProjectTask: (projectId, taskId, updates) => {
        const selectedTask = get().selectedProjectTasks[projectId]
        if (selectedTask?.id !== taskId) return

        set(state => ({
          selectedProjectTasks: {
            ...state.selectedProjectTasks,
            [projectId]: {
              ...selectedTask,
              ...updates,
            },
          },
        }))
      },

      setProjectInfoOpen: (projectId, open) =>
        set(state => ({
          projectInfoOpen: {
            ...state.projectInfoOpen,
            [projectId]: open,
          },
        })),

      setThreadSearch: (entityId, search) =>
        set(state => ({
          threadSearchByEntityId: {
            ...state.threadSearchByEntityId,
            [entityId]: search,
          },
        })),

      setThreadDetailMobileOpen: (entityId, open) =>
        set(state => ({
          threadDetailMobileOpenByEntityId: {
            ...state.threadDetailMobileOpenByEntityId,
            [entityId]: open,
          },
        })),

      resetPortalUi: entityId =>
        set(state => {
          if (!entityId) {
            return {
              mobileSidebarOpen: false,
              listFiltersByEntityId: {},
              activeProjectTabs: {},
              taskViewModes: {},
              selectedProjectTasks: {},
              projectInfoOpen: {},
              threadSearchByEntityId: {},
              threadDetailMobileOpenByEntityId: {},
            }
          }

          const { [entityId]: _listFilters, ...listFiltersByEntityId } =
            state.listFiltersByEntityId
          const { [entityId]: _threadSearch, ...threadSearchByEntityId } =
            state.threadSearchByEntityId
          const {
            [entityId]: _threadDetail,
            ...threadDetailMobileOpenByEntityId
          } = state.threadDetailMobileOpenByEntityId

          return {
            listFiltersByEntityId,
            threadSearchByEntityId,
            threadDetailMobileOpenByEntityId,
          }
        }),
    }),
    {
      name: 'client-portal-ui-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({
        listFiltersByEntityId: state.listFiltersByEntityId,
        activeProjectTabs: state.activeProjectTabs,
        taskViewModes: state.taskViewModes,
        desktopSidebarCollapsed: state.desktopSidebarCollapsed,
      }),
    },
  ),
)

export const useClientPortalStore = createSelectors(store)
export { DEFAULT_LIST_FILTERS as CLIENT_PORTAL_DEFAULT_LIST_FILTERS }
